import React, { useState, useEffect } from 'react';
import { CASHBACK_SITES, getSupplierLogo } from '../consts';
import DealCard from './DealCard';

// Copy Button Component
export const CopyButton = ({ text }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <button
            onClick={handleCopy}
            className="text-teal-600 hover:text-teal-800 transition"
            title={copied ? 'Copied!' : 'Copy'}
        >
            {copied ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
            ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
            )}
        </button>
    );
};

// Pack Size Button Component
export const PackSizeButton = ({ packSize, onPackSizeChange }) => {
    const [showInput, setShowInput] = useState(false);
    const [tempValue, setTempValue] = useState(packSize);

    const handleSave = () => {
        const size = parseInt(tempValue) || 1;
        onPackSizeChange(size >= 1 ? size : 1);
        setShowInput(false);
    };

    return (
        <div className="relative">
            <button
                onClick={() => setShowInput(true)}
                className="text-purple-600 hover:text-purple-800 transition flex items-center"
                title="Set pack size multiplier"
            >
                <span className="text-xs">📦</span>
                {packSize > 1 && <span className="text-xs font-bold">×{packSize}</span>}
            </button>

            {showInput && (
                <div className="absolute top-full left-0 mt-1 bg-white border-2 border-purple-300 rounded-lg shadow-xl p-3 z-50 min-w-[200px]"
                    onClick={(e) => e.stopPropagation()}>
                    <div className="text-xs font-semibold text-gray-700 mb-2">Pack Size Multiplier</div>
                    <div className="text-[10px] text-gray-600 mb-2">
                        If supplier sells singles but listing is 2-pack/3-pack, enter the pack size to auto-calculate cost
                    </div>
                    <input
                        type="number"
                        min="1"
                        value={tempValue}
                        onChange={(e) => setTempValue(e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm mb-2"
                        placeholder="Pack size (e.g., 2 for 2-pack)"
                        autoFocus
                    />
                    <div className="flex gap-2">
                        <button
                            onClick={handleSave}
                            className="flex-1 px-2 py-1 bg-purple-600 text-white rounded text-xs font-semibold hover:bg-purple-700"
                        >
                            Apply
                        </button>
                        <button
                            onClick={() => setShowInput(false)}
                            className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// Fee Settings Modal Component
export const FeeSettings = ({ marketplace, onClose }) => {
    const [settings, setSettings] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadSettings = async () => {
            const storageKey = `${marketplace}_fee_settings`;
            const saved = localStorage.getItem(storageKey);
            if (saved) {
                setSettings(JSON.parse(saved));
            } else {
                const defaults = {
                    amazon: { salesTax: 0, inboundShipping: 0, otherCosts: 0, placementFees: 0 },
                    walmart: { salesTax: 0, inboundShipping: 0, otherCosts: 0 },
                    ebay: { storeSubscription: 'none', topRated: false, salesTax: 0, packagingCost: 0, otherCosts: 0 }
                };
                setSettings(defaults[marketplace] || {});
            }
            setLoading(false);
        };
        loadSettings();
    }, [marketplace]);

    const saveSettings = () => {
        const storageKey = `${marketplace}_fee_settings`;
        localStorage.setItem(storageKey, JSON.stringify(settings));
        onClose(settings);
    };

    const updateSetting = (key, value) => {
        setSettings(prev => ({ ...prev, [key]: value }));
    };

    if (loading) return null;

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
            <div style={{
                backgroundColor: 'white', borderRadius: '12px', padding: '24px',
                width: '90%', maxWidth: '500px', maxHeight: '80vh', overflowY: 'auto',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
            }} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3 style={{ fontSize: '18px', fontWeight: 'bold', margin: 0, textTransform: 'capitalize' }}>
                        {marketplace} Fee Settings
                    </h3>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {marketplace === 'ebay' && (
                        <>
                            <div>
                                <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>
                                    Store Subscription
                                </label>
                                <select value={settings.storeSubscription || 'none'} onChange={(e) => updateSetting('storeSubscription', e.target.value)}
                                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}>
                                    <option value="none">No Store</option>
                                    <option value="basic">Basic Store</option>
                                    <option value="premium">Premium Store</option>
                                    <option value="anchor">Anchor Store</option>
                                    <option value="enterprise">Enterprise Store</option>
                                </select>
                            </div>
                            <div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '600', color: '#374151', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={settings.topRated || false} onChange={(e) => updateSetting('topRated', e.target.checked)}
                                        style={{ width: '18px', height: '18px' }} />
                                    Top Rated Plus Seller
                                </label>
                            </div>
                            <div style={{ height: '1px', backgroundColor: '#e5e7eb' }}></div>
                        </>
                    )}

                    {marketplace === 'amazon' && (
                        <div>
                            <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>
                                Placement Fees ($)
                            </label>
                            <input type="number" value={settings.placementFees || 0} onChange={(e) => updateSetting('placementFees', parseFloat(e.target.value) || 0)}
                                step="0.01" style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
                            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                                Minimal Split: up to $0.68, Partial Split: up to $0.48
                            </div>
                        </div>
                    )}

                    <div>
                        <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>Sales Tax (%)</label>
                        <input type="number" value={settings.salesTax || 0} onChange={(e) => updateSetting('salesTax', parseFloat(e.target.value) || 0)}
                            step="0.1" style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
                    </div>

                    {marketplace !== 'ebay' && (
                        <div>
                            <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>Inbound Shipping ($/lb)</label>
                            <input type="number" value={settings.inboundShipping || 0} onChange={(e) => updateSetting('inboundShipping', parseFloat(e.target.value) || 0)}
                                step="0.01" style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
                        </div>
                    )}

                    {marketplace === 'ebay' && (
                        <div>
                            <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>Packaging Cost ($)</label>
                            <input type="number" value={settings.packagingCost || 0} onChange={(e) => updateSetting('packagingCost', parseFloat(e.target.value) || 0)}
                                step="0.01" style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
                        </div>
                    )}

                    <div>
                        <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>Other Costs ($)</label>
                        <input type="number" value={settings.otherCosts || 0} onChange={(e) => updateSetting('otherCosts', parseFloat(e.target.value) || 0)}
                            step="0.01" style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
                    </div>

                    {marketplace !== 'ebay' && (
                        <div>
                            <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>
                                Fulfillment Cost ($) <span style={{ fontSize: '11px', color: '#6b7280' }}>(FBM/SF only)</span>
                            </label>
                            <input type="number" value={settings.fulfillmentCost || 0} onChange={(e) => updateSetting('fulfillmentCost', parseFloat(e.target.value) || 0)}
                                step="0.01" style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
                            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                                Your own shipping/handling cost when not using FBA or WFS
                            </div>
                        </div>
                    )}
                </div>

                <div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <button onClick={saveSettings} style={{
                        padding: '8px 20px', border: 'none', borderRadius: '8px', fontSize: '14px',
                        cursor: 'pointer', backgroundColor: '#0ea5e9', color: 'white', fontWeight: '600'
                    }}>
                        Save Settings
                    </button>
                </div>
            </div>
        </div>
    );
};

// Keepa Settings Modal (keeping it brief, same as before)
export const KeepaSettingsModal = ({ onClose, onSave }) => {
    const [settings, setSettings] = useState(() => {
        const saved = localStorage.getItem('keepa_chart_settings');
        return saved ? JSON.parse(saved) : { range: 90, salesrank: true, amazon: true, new: true, bb: true, fba: true };
    });

    const handleSave = () => {
        localStorage.setItem('keepa_chart_settings', JSON.stringify(settings));
        onSave(settings);
        onClose();
    };

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={handleSave}>
            <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '400px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }} onClick={(e) => e.stopPropagation()}>
                <div style={{ marginBottom: '20px' }}>
                    <h3 style={{ fontSize: '18px', fontWeight: 'bold', margin: 0 }}>Keepa Chart Settings</h3>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div>
                        <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>Time Range</label>
                        <select value={settings.range} onChange={(e) => setSettings({ ...settings, range: parseInt(e.target.value) })}
                            style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}>
                            <option value={30}>30 Days</option>
                            <option value={90}>90 Days</option>
                            <option value={180}>180 Days</option>
                            <option value={365}>1 Year</option>
                            <option value={9999}>All Time</option>
                        </select>
                    </div>
                    <div style={{ height: '1px', backgroundColor: '#e5e7eb' }}></div>
                    <div>
                        <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>Show on Chart</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {[
                                { key: 'salesrank', label: 'Sales Rank' },
                                { key: 'amazon', label: 'Amazon Price' },
                                { key: 'new', label: 'New Price' },
                                { key: 'bb', label: 'Buy Box' },
                                { key: 'fba', label: 'FBA Price' }
                            ].map(({ key, label }) => (
                                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#374151', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={settings[key]} onChange={(e) => setSettings({ ...settings, [key]: e.target.checked })}
                                        style={{ width: '18px', height: '18px' }} />
                                    {label}
                                </label>
                            ))}
                        </div>
                    </div>
                </div>
                <div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <button onClick={handleSave} style={{
                        padding: '8px 20px', border: 'none', borderRadius: '8px', fontSize: '14px',
                        cursor: 'pointer', backgroundColor: '#0ea5e9', color: 'white', fontWeight: '600'
                    }}>
                        Save Settings
                    </button>
                </div>
            </div>
        </div>
    );
};

// Cashback/Gift Card Modal Component
export const CashbackModal = ({ supplierName, onClose }) => {
    const formattedSupplier = (supplierName || '').toLowerCase().replace(/\s+/g, '-').replace(/^./, char => char.toUpperCase());

    const cashbackSites = CASHBACK_SITES.filter(site => site.type === 'cashback');
    const giftcardSites = CASHBACK_SITES.filter(site => site.type === 'giftcard');

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1001]"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-lg max-w-2xl w-full max-h-[85vh] overflow-y-auto m-4"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between z-10">
                    <h3 className="text-base font-semibold text-gray-800">
                        Cashback & Gift Cards for {supplierName}
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>

                <div className="p-4 space-y-4">
                    <div className="mb-4 text-sm text-gray-600">
                        Save money on your <span className="font-semibold">{supplierName}</span> purchase with cashback or discounted gift cards!
                    </div>
                    {/* Gift Cards */}
                    <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                            <span>🎁</span> Gift Cards
                        </h4>
                        <div className="grid grid-cols-2 gap-2">
                            {giftcardSites.map((site, idx) => (
                                <a
                                    key={idx}
                                    href={site.url.replace('{supplier}', formattedSupplier)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 p-2 border border-gray-200 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-all group"
                                >
                                    <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                                        <img
                                            src={site.logo}
                                            alt={site.name}
                                            className="w-5 h-5 object-contain"
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.parentElement.innerHTML = `<div class="w-7 h-7 bg-gradient-to-br from-purple-400 to-pink-500 rounded flex items-center justify-center text-white font-bold text-[10px]">${site.name.substring(0, 2).toUpperCase()}</div>`;
                                            }}
                                        />
                                    </div>
                                    <div className="flex-1 min-w-0 text-xs font-medium text-gray-700 group-hover:text-purple-700 truncate">
                                        {site.name}
                                    </div>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-300 group-hover:text-purple-500 flex-shrink-0">
                                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                        <polyline points="15 3 21 3 21 9"></polyline>
                                        <line x1="10" y1="14" x2="21" y2="3"></line>
                                    </svg>
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Cashback */}
                    <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                            <span>💵</span> Cashback
                        </h4>
                        <div className="grid grid-cols-2 gap-2">
                            {cashbackSites.map((site, idx) => (
                                <a
                                    key={idx}
                                    href={site.url.replace('{supplier}', formattedSupplier)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 p-2 border border-gray-200 rounded-lg hover:border-teal-400 hover:bg-teal-50 transition-all group"
                                >
                                    <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                                        <img
                                            src={site.logo}
                                            alt={site.name}
                                            className="w-5 h-5 object-contain"
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.parentElement.innerHTML = `<div class="w-7 h-7 bg-gradient-to-br from-teal-400 to-blue-500 rounded flex items-center justify-center text-white font-bold text-[10px]">${site.name.substring(0, 2).toUpperCase()}</div>`;
                                            }}
                                        />
                                    </div>
                                    <div className="flex-1 min-w-0 text-xs font-medium text-gray-700 group-hover:text-teal-700 truncate">
                                        {site.name}
                                    </div>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-300 group-hover:text-teal-500 flex-shrink-0">
                                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                        <polyline points="15 3 21 3 21 9"></polyline>
                                        <line x1="10" y1="14" x2="21" y2="3"></line>
                                    </svg>
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Multi-Source Popup Component
export const MultiSourcePopup = ({ allSources, couponAmount, fulfillmentType, onClose, onRefreshGated, refreshing, onSettingsChange, amazonLinked = false }) => {
    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-4"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-lg max-w-7xl w-full max-h-[75vh] overflow-y-auto mt-[7vh]"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
                    <h3 className="text-lg font-semibold text-gray-800">
                        Choose Your Supplier - {allSources.length} Option{allSources.length > 1 ? 's' : ''} Available
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 transition-colors"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>
                <div className="p-6 space-y-4">
                    {allSources.sort((a, b) => a.source_price - b.source_price).map((source, idx) => (
                        <div key={idx} className="border-2 border-blue-300 rounded-lg bg-blue-50 p-4">
                            <div className="text-sm font-bold text-blue-900 mb-3 flex items-center gap-2">
                                <img
                                    src={getSupplierLogo(source.supplier_name)}
                                    alt={source.supplier_name}
                                    className="w-8 h-8 object-contain mix-blend-darken"
                                    onError={(e) => e.target.style.display = 'none'}
                                />
                                <span>Buy from {source.supplier_name?.charAt(0).toUpperCase() + source.supplier_name?.slice(1)}</span>
                                <span className="ml-auto text-green-700">
                                    ${((source.source_price || 0) * (1 - (couponAmount / 100))).toFixed(2)}
                                </span>
                            </div>
                            <DealCard
                                deal={source}
                                alternativeSources={[]}
                                couponAmount={couponAmount}
                                fulfillmentType={fulfillmentType}
                                onRefreshGated={onRefreshGated}
                                refreshing={refreshing}
                                onSettingsChange={onSettingsChange}
                                amazonLinked={amazonLinked}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};